import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/hooks/useAuth';
import { toast } from 'sonner';

export type CurrentUserProfile = {
  id: string;
  username: string;
  name: string;
  bio?: string;
  profile_picture?: string;
  concerts_attended: number;
  created_at: string;
  updated_at: string;
  followers?: number;
  following?: number;
  show_achievements?: boolean;
  email_verified?: boolean;
};

export function useCurrentUserProfile() {
  const { user } = useAuth();
  
  return useQuery({
    queryKey: ['currentUserProfile', user?.id],
    queryFn: async () => {
      if (!user) {
        console.log('useCurrentUserProfile: No user logged in');
        return null;
      }
      
      console.log('useCurrentUserProfile: Fetching profile for user:', user.id);
      
      const { data, error } = await supabase
        .from('profiles')
        .select('*')
        .eq('id', user.id)
        .maybeSingle();

      if (error) {
        console.error('useCurrentUserProfile: Error fetching profile:', error);
        throw error;
      }
      
      console.log('useCurrentUserProfile: Profile data:', data);
      
      if (!data) {
        console.log('useCurrentUserProfile: No profile found for user');
        return null;
      }

      // Get follower and following counts
      const [followersResult, followingResult] = await Promise.all([
        supabase
          .from('followers')
          .select('id', { count: 'exact' })
          .eq('following_id', data.id),
        supabase
          .from('followers')
          .select('id', { count: 'exact' })
          .eq('follower_id', data.id)
      ]);
      
      return {
        ...data,
        followers: followersResult.count || 0,
        following: followingResult.count || 0,
      };
    },
    enabled: !!user,
    retry: 3,
    retryDelay: (attempt) => Math.min(500 * 2 ** attempt, 3000),
  });
}

export function useUpdateCurrentUserProfile() {
  const queryClient = useQueryClient();
  const { user } = useAuth();
  
  return useMutation({
    mutationFn: async (profileData: Partial<CurrentUserProfile>) => {
      if (!user) throw new Error('User not authenticated');
      
      // Check if profile exists
      const { data: existingProfile } = await supabase
        .from('profiles')
        .select('id')
        .eq('id', user.id)
        .maybeSingle();

      // Build data object with defaults for new profile
      const dataToUpsert: Record<string, any> = { 
        id: user.id,
        name: profileData.name ?? (existingProfile ? undefined : (user.user_metadata?.name || 'User')),
        username: profileData.username ?? (existingProfile ? undefined : (user.user_metadata?.username || `user_${user.id.slice(0, 8)}`))
      };
      
      // Add optional fields if provided
      if (profileData.bio !== undefined) dataToUpsert.bio = profileData.bio;
      if (profileData.profile_picture !== undefined) dataToUpsert.profile_picture = profileData.profile_picture;
      if ((profileData as any).show_achievements !== undefined) dataToUpsert.show_achievements = (profileData as any).show_achievements;
      
      // Perform explicit update or insert to avoid relying on ON CONFLICT
      let data, error;
      if (existingProfile) {
        const updateData: Record<string, any> = {};
        if (profileData.name !== undefined) updateData.name = profileData.name;
        if (profileData.username !== undefined) updateData.username = profileData.username;
        if (profileData.bio !== undefined) updateData.bio = profileData.bio;
        if (profileData.profile_picture !== undefined) updateData.profile_picture = profileData.profile_picture;
        if ((profileData as any).show_achievements !== undefined) updateData.show_achievements = (profileData as any).show_achievements;

        ({ data, error } = await supabase
          .from('profiles')
          .update(updateData as any)
          .eq('id', user.id)
          .select()
          .single());
      } else {
        const insertData: Record<string, any> = {
          id: user.id,
          name: profileData.name ?? (user.user_metadata?.name || 'User'),
          username: profileData.username ?? (user.user_metadata?.username || `user_${user.id.slice(0, 8)}`)
        };
        if (profileData.bio !== undefined) insertData.bio = profileData.bio;
        if (profileData.profile_picture !== undefined) insertData.profile_picture = profileData.profile_picture;
        if ((profileData as any).show_achievements !== undefined) insertData.show_achievements = (profileData as any).show_achievements;

        ({ data, error } = await supabase
          .from('profiles')
          .insert(insertData as any)
          .select()
          .single());
      }
      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['currentUserProfile'] });
      toast.success('Profile updated successfully!');
    },
    onError: (error) => {
      toast.error('Failed to update profile: ' + (error as any)?.message);
    },
  });
}

export function useUploadProfilePicture() {
  const queryClient = useQueryClient();
  const { user } = useAuth();
  
  return useMutation({
    mutationFn: async (file: File) => {
      if (!user) throw new Error('User not authenticated');
      
      console.log('Starting upload process for:', file.name, file.type, file.size);
      
      // Create a unique filename
      const fileExt = file.name.split('.').pop();
      const fileName = `${user.id}/avatar.${fileExt}`;
      
      console.log('Uploading to path:', fileName);
      
      // Upload the file to Supabase Storage
      const { data: uploadData, error: uploadError } = await supabase.storage
        .from('profile-pictures')
        .upload(fileName, file, {
          cacheControl: '3600',
          upsert: true
        });

      console.log('Upload result:', { uploadData, uploadError });
      if (uploadError) {
        console.error('Upload error details:', uploadError);
        throw uploadError;
      }

      // Get the public URL
      const { data: { publicUrl } } = supabase.storage
        .from('profile-pictures')
        .getPublicUrl(fileName);

      console.log('Generated public URL:', publicUrl);

      // Update or insert the profile with the new picture URL (avoid ON CONFLICT)
      const { data: existingProfile } = await supabase
        .from('profiles')
        .select('id')
        .eq('id', user.id)
        .maybeSingle();

      let data, error;
      if (existingProfile) {
        ({ data, error } = await supabase
          .from('profiles')
          .update({ profile_picture: publicUrl } as any)
          .eq('id', user.id)
          .select()
          .single());
      } else {
        ({ data, error } = await supabase
          .from('profiles')
          .insert({ id: user.id, profile_picture: publicUrl } as any)
          .select()
          .single());
      }
      console.log('Profile update result:', { data, error });
      if (error) {
        console.error('Profile update error:', error);
        throw error;
      }
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['currentUserProfile'] });
      toast.success('Profile picture updated successfully!');
    },
    onError: (error) => {
      console.error('Upload mutation error:', error);
      toast.error('Failed to upload profile picture: ' + error.message);
    },
  });
}